import { Controller } from "@hotwired/stimulus"
import { DataSet, Timeline } from "vis-timeline/standalone"

const SIDE_PANEL_STORAGE_KEY = "jpm.timelineDetailsCollapsed"

export default class extends Controller {
  static targets = [
    "canvas",
    "feedback",
    "layout",
    "sidePanel",
    "sidePanelBody",
    "collapsePanelButton",
    "collapsePanelLabel",
    "expandPanelButton",
    "statusFilter",
    "priorityFilter",
    "riskFilter"
  ]
  static values = { url: String, canEdit: Boolean }

  connect() {
    this.instance = null
    this.groupDs = null
    this.itemDs = null
    this.rawItems = []
    this.redrawFrame = null
    this.sidePanelCollapsed = this.readSidePanelCollapsed()
    this.applySidePanelCollapsed()
    this.resizeObserver = new ResizeObserver(() => this.scheduleRedraw())
    if (this.hasLayoutTarget) {
      this.resizeObserver.observe(this.layoutTarget)
    }
    this.load()
  }

  disconnect() {
    this.resizeObserver?.disconnect()
    if (this.redrawFrame) cancelAnimationFrame(this.redrawFrame)
    if (this.instance) {
      this.instance.destroy()
      this.instance = null
    }
  }

  async load() {
    this.canvasTarget.innerHTML = ""
    const loading = document.createElement("p")
    loading.className = "flex min-h-[320px] items-center justify-center px-4 text-sm text-zinc-500"
    loading.textContent = "Loading timeline…"
    this.canvasTarget.appendChild(loading)

    try {
      const response = await fetch(this.urlValue, {
        headers: { Accept: "application/json" },
        credentials: "same-origin"
      })
      if (!response.ok) {
        loading.textContent = "Could not load timeline data."
        loading.classList.add("text-red-600")
        return
      }
      const payload = await response.json()
      const groups = payload.groups || []
      const items = payload.items || []
      const meta = payload.meta || {}
      this.meta = meta
      this.rawItems = items

      loading.remove()

      if (groups.length === 0 && items.length === 0) {
        const empty = document.createElement("p")
        empty.className = "flex min-h-[320px] items-center justify-center px-4 text-center text-sm text-zinc-500"
        empty.textContent = "No scheduled tasks yet. Add start dates below."
        this.canvasTarget.appendChild(empty)
        return
      }

      this.groupDs = new DataSet(groups)
      this.itemDs = new DataSet(items)

      const options = {
        stack: true,
        zoomMin: 1000 * 60 * 60 * 24,
        orientation: "top",
        selectable: true,
        editable: {
          updateTime: Boolean(meta.editable),
          updateGroup: Boolean(meta.editable),
          remove: false,
          add: false
        },
        margin: { item: 10, axis: 6 },
        onMove: (item, callback) => this.handleMove(item, callback)
      }

      this.instance = new Timeline(this.canvasTarget, this.itemDs, this.groupDs, options)
      this.instance.on("select", (props) => this.handleSelect(props))
      this.instance.on("doubleClick", (props) => this.handleSelect(props))
      this.feedbackTarget.textContent = meta.editable
        ? "Click a bar to inspect details. Drag to reschedule."
        : "Read-only mode. Click a bar to inspect details."
      this.fitRange()
      this.redrawTimeline()
    } catch (err) {
      loading.textContent = "Timeline failed to render."
      loading.classList.add("text-red-600")
      console.error(err)
    }
  }

  resetFilters() {
    this.statusFilterTarget.value = "all"
    this.priorityFilterTarget.value = "all"
    this.riskFilterTarget.value = "all"
    this.applyFilters()
  }

  applyFilters() {
    if (!this.itemDs) return
    const status = this.statusFilterTarget.value
    const priority = this.priorityFilterTarget.value
    const risk = this.riskFilterTarget.value

    const filtered = this.rawItems.filter((item) => {
      if (status !== "all" && item.status !== status) return false
      if (priority !== "all" && item.priority !== priority) return false

      const risks = item.risk_flags || []
      if (risk === "high" && !risks.some((r) => r === "overdue" || r === "dependency_conflict")) return false
      if (risk === "medium" && !risks.some((r) => r === "due_soon" || r === "capacity_warning" || r === "overlap")) return false
      if (risk === "none" && risks.length > 0) return false
      return true
    })

    this.itemDs.clear()
    this.itemDs.add(filtered)
    this.feedbackTarget.textContent = `Showing ${filtered.length} task bar${filtered.length === 1 ? "" : "s"}`
  }

  fitRange() {
    if (!this.instance || !this.rawItems.length) return
    const starts = this.rawItems.map((item) => new Date(item.start).getTime())
    const ends = this.rawItems.map((item) => new Date(item.end).getTime())
    const min = Math.min(...starts)
    const max = Math.max(...ends)
    this.instance.setWindow(new Date(min - 3 * 24 * 60 * 60 * 1000), new Date(max + 3 * 24 * 60 * 60 * 1000), { animation: true })
  }

  handleSelect(props) {
    if (!props.items || props.items.length === 0) return
    const item = this.itemDs.get(props.items[0])
    if (!item) return
    this.feedbackTarget.textContent = `Selected: ${item.task_name || item.content}`
    this.feedbackTarget.classList.remove("text-red-600")
    if (this.sidePanelCollapsed) {
      this.setSidePanelCollapsed(false)
    }
    this.renderPanel(item)
    if (!this.sidePanelCollapsed) {
      this.sidePanelTarget?.scrollIntoView({ behavior: "smooth", block: "nearest" })
    }
  }

  toggleSidePanel() {
    this.setSidePanelCollapsed(!this.sidePanelCollapsed)
  }

  setSidePanelCollapsed(collapsed) {
    this.sidePanelCollapsed = collapsed
    this.applySidePanelCollapsed()
    try {
      localStorage.setItem(SIDE_PANEL_STORAGE_KEY, collapsed ? "1" : "0")
    } catch (_err) {
      // ignore private browsing / quota errors
    }
    this.redrawTimeline()
  }

  applySidePanelCollapsed() {
    const collapsed = this.sidePanelCollapsed

    if (this.hasLayoutTarget) {
      this.layoutTarget.dataset.sidePanelCollapsed = collapsed ? "true" : "false"
    }

    if (this.hasSidePanelTarget) {
      this.sidePanelTarget.classList.toggle("hidden", collapsed)
      this.sidePanelTarget.setAttribute("aria-hidden", collapsed ? "true" : "false")
    }

    if (this.hasCollapsePanelButtonTarget) {
      this.collapsePanelButtonTarget.setAttribute("aria-expanded", collapsed ? "false" : "true")
    }

    if (this.hasExpandPanelButtonTarget) {
      this.expandPanelButtonTarget.classList.toggle("hidden", !collapsed)
    }

    if (this.hasFeedbackTarget) {
      this.feedbackTarget.classList.toggle("pr-28", collapsed)
    }

    if (this.hasCollapsePanelLabelTarget) {
      this.collapsePanelLabelTarget.textContent = collapsed ? "Show panel" : "Hide panel"
    }
  }

  readSidePanelCollapsed() {
    try {
      return localStorage.getItem(SIDE_PANEL_STORAGE_KEY) === "1"
    } catch (_err) {
      return false
    }
  }

  redrawTimeline() {
    this.scheduleRedraw()
  }

  scheduleRedraw() {
    if (!this.instance) return
    if (this.redrawFrame) cancelAnimationFrame(this.redrawFrame)
    this.redrawFrame = requestAnimationFrame(() => {
      this.redrawFrame = requestAnimationFrame(() => {
        this.instance?.redraw()
        this.redrawFrame = null
      })
    })
  }

  renderPanel(item) {
    const risks = item.risk_flags || []
    const riskLabel = risks.length ? risks.map((r) => r.replaceAll("_", " ")).join(", ") : "No active warnings"
    const schedule =
      item.start_on && item.end_on
        ? `${item.start_on} to ${item.end_on}`
        : item.start_on
          ? `${item.start_on} → open-ended`
          : "No dates set"

    this.sidePanelBodyTarget.innerHTML = `
      <div class="space-y-3">
        <div>
          <p class="text-xs uppercase tracking-wide text-zinc-500">Task</p>
          <p class="text-sm font-semibold text-zinc-900">${this.escapeHtml(item.task_name || item.content || "Task")}</p>
          <p class="text-xs text-zinc-500">${this.escapeHtml(item.owner || "Unassigned contractor")}</p>
        </div>
        <div class="grid grid-cols-2 gap-2 text-xs">
          <div class="rounded border border-zinc-200 bg-zinc-50 p-2"><span class="block text-zinc-500">Status</span><span class="font-medium text-zinc-900">${this.escapeHtml((item.status || "n/a").replaceAll("_", " "))}</span></div>
          <div class="rounded border border-zinc-200 bg-zinc-50 p-2"><span class="block text-zinc-500">Priority</span><span class="font-medium text-zinc-900">${this.escapeHtml(item.priority || "n/a")}</span></div>
        </div>
        <p class="text-sm text-zinc-700"><span class="font-medium">Schedule:</span> ${this.escapeHtml(schedule)}</p>
        <p class="text-sm text-zinc-700"><span class="font-medium">Dependencies:</span> ${this.escapeHtml(item.dependency_summary || "No prerequisites")}</p>
        <p class="text-sm text-zinc-700"><span class="font-medium">Risks:</span> ${this.escapeHtml(riskLabel)}</p>
        <div class="flex flex-wrap gap-2 pt-1">
          <a href="${this.escapeHtml(item.task_url || "#")}" class="jpm-btn-secondary">Open task</a>
        </div>
      </div>
    `
  }

  async handleMove(item, callback) {
    if (!this.meta?.editable) {
      callback(null)
      return
    }
    try {
      const url = this.meta.update_item_url_template.replace("__ID__", String(item.task_id || item.id).replace("task-", ""))
      const token = document.querySelector('meta[name="csrf-token"]')?.content
      const response = await fetch(url, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": token
        },
        credentials: "same-origin",
        body: JSON.stringify({
          timeline_task: {
            start_on: this.isoDate(item.start),
            end_on: this.isoDate(item.end),
            group: item.group
          }
        })
      })
      const payload = await response.json()
      if (!response.ok || !payload.ok) {
        this.feedbackTarget.textContent = payload.error || "Could not update timeline item."
        this.feedbackTarget.classList.add("text-red-600")
        callback(null)
        return
      }
      const updated = { ...item, ...payload.item }
      this.upsertRawItem(updated)
      this.feedbackTarget.textContent = "Timeline item updated."
      this.feedbackTarget.classList.remove("text-red-600")
      callback(updated)
      this.renderPanel(updated)
    } catch (err) {
      this.feedbackTarget.textContent = "Could not update timeline item."
      this.feedbackTarget.classList.add("text-red-600")
      callback(null)
    }
  }

  upsertRawItem(updated) {
    const idx = this.rawItems.findIndex((row) => row.id === updated.id)
    if (idx === -1) {
      this.rawItems.push(updated)
    } else {
      this.rawItems[idx] = { ...this.rawItems[idx], ...updated }
    }
  }

  isoDate(value) {
    const d = new Date(value)
    if (Number.isNaN(d.getTime())) return ""
    return d.toISOString().slice(0, 10)
  }

  escapeHtml(value) {
    return String(value)
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#39;")
  }
}
